import {memo, useCallback, useMemo, useState} from "react";
import {Badge, Box, Flex, HStack, Icon, IconButton, Separator, Stack, Text, VStack} from "@chakra-ui/react";
import {
  DocumentTextIcon,
  EllipsisVerticalIcon,
  ListBulletIcon,
  PhotoIcon,
  PlusIcon,
  VideoCameraIcon,
} from "@heroicons/react/16/solid";
import {DocumentIcon} from "@heroicons/react/20/solid";
import {Button} from "../../../../../components/ui/button";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../../../components/ui/menu";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from "../../../../../components/ui/drawer";
import {
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from "../../../../../components/ui/accordion";
import type {MockEvidence, MockEvidenceSourceType, MockQuestionnaire} from "../types";
import {PROCESS_EVIDENCE, PROCESS_QUESTIONNAIRES} from "../mockData";
import type {ProcessHeaderProps} from "../types";
import {ProcessHeader} from "./ProcessHeader";
import {QuestionnaireDrawer, ExternalQuestionnairesTable, InternalQuestionnairesTable} from "./QuestionnaireTables";
import {actionTriggerStyles} from "./sharedStyles";

// Source type icon/color map — same values as SOURCE_TYPE_INFO in Assessment/utils.ts
const SOURCE_TYPE_INFO: Record<MockEvidenceSourceType, {label: string; icon: typeof DocumentTextIcon; color: string}> =
  {
    Document: {label: "Document", icon: DocumentTextIcon, color: "blue.500"},
    Image: {label: "Image", icon: PhotoIcon, color: "purple.500"},
    Video: {label: "Video", icon: VideoCameraIcon, color: "red.500"},
    Questionnaire: {label: "Client request", icon: ListBulletIcon, color: "green.500"},
  };

// --- Evidence Drawer (placeholder) ---

export const EvidenceDrawer = memo(
  ({evidence, open, onClose}: {evidence: MockEvidence | null; open: boolean; onClose: () => void}) => {
    if (!evidence) return null;
    const info = SOURCE_TYPE_INFO[evidence.sourceType];

    return (
      <DrawerRoot open={open} onOpenChange={d => !d.open && onClose()} placement="end" size="md">
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerCloseTrigger />
          <DrawerHeader>
            <HStack gap={2}>
              <Icon asChild boxSize="1.25em" color={info.color}>
                <info.icon />
              </Icon>
              <DrawerTitle>{evidence.name}</DrawerTitle>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <Stack gap={4}>
              <HStack gap={4} fontSize="sm">
                <HStack gap={1}>
                  <Text color="fg.muted">Type:</Text>
                  <Text>{info.label}</Text>
                </HStack>
                <HStack gap={1}>
                  <Text color="fg.muted">Scope:</Text>
                  <Text>{evidence.scope}</Text>
                </HStack>
              </HStack>
              {evidence.context && (
                <Box>
                  <Text fontSize="sm" color="fg.muted" mb={1}>
                    Context
                  </Text>
                  <Text fontSize="sm">{evidence.context}</Text>
                </Box>
              )}
              <Box border="1px dashed" borderColor="gray.300" borderRadius="md" p={8} textAlign="center" mt={4}>
                <Text fontSize="sm" color="fg.muted">
                  Source preview coming soon
                </Text>
              </Box>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </DrawerRoot>
    );
  },
);

// --- Citation Badge ---

export const CitationBadge = memo(({citationId, onClick}: {citationId: number; onClick: () => void}) => (
  <Badge
    colorPalette="blue"
    size="sm"
    cursor="pointer"
    _hover={{bg: "blue.600", color: "white"}}
    onClick={onClick}
    css={{verticalAlign: "super", fontSize: "0.7em", padding: "0.1em 0.4em", marginLeft: "0.1em", marginRight: "0.1em"}}
  >
    {citationId}
  </Badge>
));

// --- Mock Evidence Item ---

const MockEvidenceItem = memo(
  ({evidence, onRemove, onView}: {evidence: MockEvidence; onRemove: (id: string) => void; onView: () => void}) => {
    const info = SOURCE_TYPE_INFO[evidence.sourceType];

    return (
      <Stack p={3} gap={0}>
        <HStack gap={6} alignItems="center">
          {/* Left: Citation ID + Icon + Name + Metadata */}
          <HStack gap={3} flex={1} w={0} minW={0} alignItems="center">
            <Badge colorPalette="blue" size="sm" variant="subtle" flexShrink={0}>
              {evidence.citationId}
            </Badge>
            <Icon asChild boxSize="1.25em" color={info.color} flexShrink={0}>
              <info.icon />
            </Icon>
            <Stack gap={0} flex={1} w={0} minW={0}>
              <Text fontSize="sm" fontWeight="medium" color="fg.default" truncate>
                {evidence.name}
              </Text>
              <HStack gap={1.5} fontSize="xs" color="fg.default">
                <HStack gap={1} flexShrink={0}>
                  <Text color="fg.muted">Type:</Text>
                  <Text>{info.label}</Text>
                </HStack>
                <Text flexShrink={0}>&middot;</Text>
                <HStack gap={1} flexShrink={0}>
                  <Text color="fg.muted">Scope:</Text>
                  <Text>{evidence.scope}</Text>
                </HStack>
                {evidence.context && (
                  <>
                    <Text flexShrink={0}>&middot;</Text>
                    <HStack gap={1} flex={1} minW={0}>
                      <Text color="fg.muted" flexShrink={0}>
                        Context:
                      </Text>
                      <Text truncate>{evidence.context}</Text>
                    </HStack>
                  </>
                )}
              </HStack>
            </Stack>
          </HStack>

          {/* Right: View + Kebab */}
          <HStack gap={1} flexShrink={0}>
            <Button size="xs" variant="outline" onClick={onView}>
              View
            </Button>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton size="xs" variant="ghost" aria-label="More actions">
                  <Icon asChild>
                    <EllipsisVerticalIcon />
                  </Icon>
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                <MenuItem
                  value="delete"
                  color="fg.error"
                  _hover={{bg: "bg.error", color: "fg.error"}}
                  onClick={() => onRemove(evidence.id)}
                >
                  Remove
                </MenuItem>
              </MenuContent>
            </MenuRoot>
          </HStack>
        </HStack>
      </Stack>
    );
  },
);

// --- Mock Evidence Section ---

export const MockEvidenceSection = memo(
  ({
    initialEvidence,
    onViewEvidence,
  }: {
    initialEvidence: MockEvidence[];
    onViewEvidence: (evidence: MockEvidence) => void;
  }) => {
    const [evidence, setEvidence] = useState<MockEvidence[]>(initialEvidence);

    const addEvidence = useCallback(() => {
      const id = `ev-mock-${Date.now()}`;
      const nextCitationId = evidence.length > 0 ? Math.max(...evidence.map(e => e.citationId)) + 1 : 1;
      setEvidence(prev => [
        ...prev,
        {
          id,
          citationId: nextCitationId,
          name: `New Evidence Document ${prev.length + 1}`,
          sourceType: "Document",
          scope: "General",
          context: "Added manually",
        },
      ]);
    }, [evidence]);

    const removeEvidence = useCallback((id: string) => {
      setEvidence(prev => prev.filter(e => e.id !== id));
    }, []);

    return (
      <Stack p={6} gap={3}>
        <HStack px={4} py={1} mt={-2} justify="end">
          <HStack as="button" gap={1} {...actionTriggerStyles} onClick={addEvidence}>
            <Box as={PlusIcon} boxSize="16px" />
            Add evidence
          </HStack>
        </HStack>

        {evidence.length > 0 ? (
          <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" overflow="hidden">
            <Stack gap={0} separator={<Separator />}>
              {evidence.map(item => (
                <MockEvidenceItem
                  key={item.id}
                  evidence={item}
                  onRemove={removeEvidence}
                  onView={() => onViewEvidence(item)}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Box border="1px dashed" borderColor="blue.300" borderRadius="md" p={6} textAlign="center" bg="blue.50">
            <VStack gap={2}>
              <Icon boxSize="1em" color="blue.400" size="md">
                <DocumentIcon />
              </Icon>
              <Text fontSize="sm" color="blue.700">
                No evidence documents added yet
              </Text>
              <Text fontSize="xs" color="blue.600">
                Add documents from the data room to support this process
              </Text>
            </VStack>
          </Box>
        )}
      </Stack>
    );
  },
);

// --- Process Questionnaires ---

export const ProcessQuestionnaires = memo(({headerProps}: {headerProps: ProcessHeaderProps}) => {
  const processId = headerProps.process.id;
  const initial = PROCESS_QUESTIONNAIRES[processId] ?? [];
  const [items, setItems] = useState<MockQuestionnaire[]>(initial);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<MockQuestionnaire | null>(null);
  const [drawerEvidence, setDrawerEvidence] = useState<MockEvidence | null>(null);

  const externals = useMemo(() => items.filter(i => i.type === "external"), [items]);
  const internals = useMemo(() => items.filter(i => i.type === "internal"), [items]);

  const openDrawer = useCallback((item: MockQuestionnaire) => setSelectedQuestionnaire(item), []);
  const closeDrawer = useCallback(() => setSelectedQuestionnaire(null), []);

  const openEvidenceDrawer = useCallback((ev: MockEvidence) => setDrawerEvidence(ev), []);
  const closeEvidenceDrawer = useCallback(() => setDrawerEvidence(null), []);

  const nextCitationId = useCallback(
    (prev: MockQuestionnaire[]) => {
      const evidenceMax = (PROCESS_EVIDENCE[processId] ?? []).reduce((max, e) => Math.max(max, e.citationId), 0);
      const questionnaireMax = prev.reduce((max, q) => Math.max(max, q.questionnaire.citationId), 0);
      return Math.max(evidenceMax, questionnaireMax) + 1;
    },
    [processId],
  );

  const addExternal = useCallback(() => {
    const id = crypto.randomUUID();
    setItems(prev => [
      ...prev,
      {
        type: "external",
        questionnaire: {
          id,
          citationId: nextCitationId(prev),
          templateName: "New External Questionnaire",
          status: "draft",
          createdAt: new Date(),
          submittedAt: null,
          lockedAt: null,
          responseCount: 0,
          totalQuestions: 0,
          questions: [],
        },
      },
    ]);
  }, [nextCitationId]);

  const addInternal = useCallback(() => {
    const id = crypto.randomUUID();
    setItems(prev => [
      ...prev,
      {
        type: "internal",
        questionnaire: {
          id,
          citationId: nextCitationId(prev),
          title: "New Internal Questionnaire",
          status: "not-started",
          assignee: "Unassigned",
          completedCount: 0,
          totalQuestions: 0,
          createdAt: new Date(),
          completedAt: null,
          questions: [],
        },
      },
    ]);
  }, [nextCitationId]);

  return (
    <Flex direction="column" minH={0} flex={1}>
      <ProcessHeader {...headerProps} />
      <AccordionRoot multiple defaultValue={["external", "internal", "evidence"]} flex={1}>
        <AccordionItem value="external">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              External Questionnaires
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent p={0} bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <Box p={6}>
              <ExternalQuestionnairesTable items={externals} onOpen={openDrawer} onAdd={addExternal} />
            </Box>
          </AccordionItemContent>
        </AccordionItem>

        <AccordionItem value="internal">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Internal Questionnaires
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent p={0} bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <Box p={6}>
              <InternalQuestionnairesTable items={internals} onOpen={openDrawer} onAdd={addInternal} />
            </Box>
          </AccordionItemContent>
        </AccordionItem>

        <AccordionItem value="evidence">
          <AccordionItemTrigger px={6} py={4} indicatorPlacement="start">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Relevant Evidence
            </Text>
          </AccordionItemTrigger>
          <AccordionItemContent bg="gray.50" borderTop="1px solid {colors.gray.200}">
            <MockEvidenceSection
              initialEvidence={PROCESS_EVIDENCE[processId] ?? []}
              onViewEvidence={openEvidenceDrawer}
            />
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>
      <QuestionnaireDrawer item={selectedQuestionnaire} open={selectedQuestionnaire !== null} onClose={closeDrawer} />
      <EvidenceDrawer evidence={drawerEvidence} open={drawerEvidence !== null} onClose={closeEvidenceDrawer} />
    </Flex>
  );
});
